import { useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Palette, Upload, Trash2, Save, ImageOff } from "lucide-react";

export const BrandingCard = ({ data, onSaved }) => {
  const [name, setName] = useState(data.display_name || "");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const logoUrl = data.logo ? `${process.env.REACT_APP_BACKEND_URL}/api/branding/logo?v=${encodeURIComponent(data.logo.uploaded_at)}` : null;

  const save = async () => {
    try { const { data: d } = await api.put("/admin/branding", { display_name: name }); onSaved(d); toast.success("Workspace branding saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try { const { data: d } = await api.post("/admin/branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } }); onSaved(d); toast.success("Logo uploaded"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const remove = async () => {
    try { const { data: d } = await api.delete("/admin/branding/logo"); onSaved(d); toast.success("Logo removed"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4 max-w-3xl" data-testid="branding-card">
      <div><p className="overline">Workspace branding</p><h2 className="text-base font-semibold text-slate-900 flex items-center gap-2"><Palette className="h-4 w-4 text-slate-500" />Logo & display name</h2><p className="text-xs text-slate-500 mt-1">Shown in the header of every exported PDF and Excel report (project and prototype reports).</p></div>
      <div className="grid sm:grid-cols-[160px_1fr] gap-5 items-start">
        <div className="space-y-2">
          <div className="h-28 w-40 rounded-lg border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center overflow-hidden" data-testid="branding-logo-preview">
            {logoUrl ? <img src={logoUrl} alt="Workspace logo" className="max-h-24 max-w-[150px] object-contain" crossOrigin="use-credentials" data-testid="branding-logo-img" /> : <div className="text-xs text-slate-400 flex flex-col items-center gap-1"><ImageOff className="h-5 w-5" />No logo</div>}
          </div>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="branding-logo-input" />
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="branding-logo-upload-btn"><Upload className="h-3.5 w-3.5 mr-1.5" />{data.logo ? "Replace" : "Upload"}</Button>
            {data.logo && <Button size="sm" variant="ghost" className="text-red-600" onClick={remove} data-testid="branding-logo-remove-btn"><Trash2 className="h-3.5 w-3.5" /></Button>}
          </div>
          <p className="text-[11px] text-slate-500">PNG or JPG, max 2 MB.</p>
        </div>
        <div className="space-y-3">
          <div><Label className="text-xs">Display name</Label><Input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="Your company name" data-testid="branding-display-name-input" /><p className="text-[11px] text-slate-500 mt-1">Replaces “Airava” in report headers.</p></div>
          <Button onClick={save} disabled={!name.trim() || name.trim() === data.display_name} className="bg-[#2563EB] hover:bg-[#1d4ed8]" data-testid="branding-save-btn"><Save className="h-4 w-4 mr-2" />Save branding</Button>
        </div>
      </div>
    </div>
  );
};
